import { useParams } from 'react-router-dom';

import styled from 'styled-components';
import { PlatformCode } from 'type/common';
import { ICreatorBasicProfileDB, ICreatorProfileDB } from 'type/wconcepdb';

import { useGetCreatorProfile } from 'hooks/queries/creator';
import CorePictureAvatar from 'shared/avatar/CorePictureAvatar';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import CoreTooltip from 'shared/CoreTooltip';
import Icon from 'shared/icons';
import { Theme } from 'styles/DefaultTheme';
import {
	ReportSection,
	ReportSectionCard,
	ReportSectionHeader,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';
import { platform_EN_name_mapper } from 'utils/platformMapper';
import round from 'utils/round';

const BasicProfile = () => {
	// creator/:platform/:recommendationId/:creatorPk

	const { platform, recommendationId, creatorPk } = useParams();

	const { data: creatorProfile, isLoading: creatorProfileLoading } = useGetCreatorProfile(
		recommendationId ?? '',
		creatorPk ?? '',
	);

	const dataSource = creatorProfile ?? ({} as ICreatorProfileDB);

	const profileDataSource = dataSource.profile;

	if (creatorProfileLoading || !profileDataSource)
		return (
			<ReportSection>
				<ReportSectionHeader>기본 프로필</ReportSectionHeader>
				<Text typo="p" _margin="0 0 16px 0">
					크리에이터 기본 정보입니다.
				</Text>

				<Grid flex="start" direction="column" _gap="16px">
					<Grid flex="between" _gap="16px" _height="120px">
						<ReportSectionCard animation={true} _width="70%">
							<Grid _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>

						<ReportSectionCard animation={true} _width="30%" delay="0.5s">
							<Grid _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
					</Grid>
				</Grid>
			</ReportSection>
		);

	if (platform === 'ig') {
		return <InstagramProfile data={profileDataSource} />;
	}
	if (platform === 'yt') {
		return <YoutubeProfile data={profileDataSource} />;
	}

	return <></>;
};

const InstagramProfile = ({ data }: { data: ICreatorBasicProfileDB }) => {
	return (
		<ReportSection>
			<ReportSectionHeader>기본 프로필</ReportSectionHeader>
			<Text typo="p" _margin="0 0 16px 0">
				크리에이터 기본 정보입니다.
			</Text>

			<Grid flex="start" direction="column" _gap="16px">
				<Grid flex="between" _gap="16px" _height="120px">
					<ReportSectionCard _width="70%">
						<Grid flex="start" _gap="16px" _height="100%">
							<CorePictureAvatar
								size="default"
								color_type="yellow"
								text={data.username ?? 'influencer'}
								picture={data.profileImage}
							/>
							<Grid _width="100%" flex="start" direction="column" _gap="2px">
								<Grid flex="start" _gap="8px" _overflow="hidden" _width="100%">
									<CoreSvg>
										<Icon.Insta />
									</CoreSvg>
									<Text typo="p_bold">{data.username ?? 'influencer'}</Text>
									<TitleWrap typo="span">{data.fullname ?? 'influencer'}</TitleWrap>
									<a href={data.link} target="_blank" rel="noreferrer">
										<CoreSvg>
											<Icon.OutLink />
										</CoreSvg>
									</a>
								</Grid>
								<Grid>
									<Text typo="span" color={Theme.colors.gray.gray80_text}>
										{data.biography ?? ''}
									</Text>
								</Grid>
							</Grid>
						</Grid>
					</ReportSectionCard>

					<ReportSectionCard _width="30%">
						<Grid flex="between" _height="100%" _gap="16px">
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'동영상'}
								</Text>
								<Text typo="p_bold">{`${round(Number(data.mediaCount), 'round')}`}</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'팔로워'}
								</Text>
								<Text typo="p_bold">{`${round(Number(data.followers), 'round')}명`}</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'팔로잉'}
								</Text>
								<Text typo="p_bold">
									{`${round(Number(data.followings), 'round')}명`}
								</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'협업 이력'}
								</Text>
								<Text typo="p_bold">
									{`${round(Number(data.collaborationCount ?? 0), 'round')}회`}
								</Text>
							</Grid>
						</Grid>
					</ReportSectionCard>
				</Grid>
			</Grid>
		</ReportSection>
	);
};
const YoutubeProfile = ({ data }: { data: ICreatorBasicProfileDB }) => {
	return (
		<ReportSection>
			<ReportSectionHeader>기본 프로필</ReportSectionHeader>
			<Text typo="p" _margin="0 0 16px 0">
				크리에이터 기본 정보입니다.
			</Text>

			<Grid flex="start" direction="column" _gap="16px">
				<Grid flex="between" _gap="16px" _height="120px">
					<ReportSectionCard _width="70%">
						<Grid flex="start" _gap="16px" _height="100%">
							<CorePictureAvatar
								size="default"
								color_type="yellow"
								text={data.username ?? 'influencer'}
								picture={data.profileImage}
							/>
							<Grid _width="100%" flex="start" direction="column" _gap="2px">
								<Grid flex="start" _gap="8px" _overflow="hidden" _width="100%">
									<CoreSvg>
										<Icon.Youtube />
									</CoreSvg>
									<Text typo="p_bold">{data.username ?? 'influencer'}</Text>
									<TitleWrap typo="span">{data.fullname ?? 'influencer'}</TitleWrap>
									<a href={data.link} target="_blank" rel="noreferrer">
										<CoreSvg>
											<Icon.OutLink />
										</CoreSvg>
									</a>
								</Grid>
								<Grid>
									<Text typo="span" color={Theme.colors.gray.gray80_text}>
										{data.biography ?? ''}
									</Text>
								</Grid>
							</Grid>
						</Grid>
					</ReportSectionCard>

					<ReportSectionCard _width="30%">
						<Grid flex="between" _height="100%" _gap="16px">
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'동영상'}
								</Text>
								<Text typo="p_bold">{`${round(Number(data.mediaCount), 'round')}`}</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'구독자'}
								</Text>
								<Text typo="p_bold">{`${round(Number(data.followers), 'round')}명`}</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{'협업 이력'}
								</Text>
								<Text typo="p_bold">
									{`${round(Number(data.collaborationCount ?? 0), 'round')}회`}
								</Text>
							</Grid>
						</Grid>
					</ReportSectionCard>
				</Grid>
			</Grid>
		</ReportSection>
	);
};

const { Grid, Text } = style;

const TitleWrap = styled(Text)`
	${({ theme }) => theme.common.ellipsis};
`;

const DesciptionWrap = styled(Text)`
	${({ theme }) => theme.common.ellipsis};
	margin-right: 8px;
`;

const RankingWrap = styled(Grid)`
	padding: 8px 16px;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
`;

const ReportGrid = styled(Grid)`
	display: grid;
	grid-template-columns: 246px 246px 1fr;
	grid-gap: 16px;
`;

const TagArrow = styled.div`
	width: 8px;
	height: 21px;

	${({ theme }) => theme.typo.body.span_bold};
	color: ${({ theme }) => theme.colors.gray.gray15};
`;

export default BasicProfile;
